import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Scale, Briefcase, FileText, AlertCircle } from 'lucide-react';

const LegalSupport = () => {
  const navigate = useNavigate();

  const legalServices = [
    {
      title: "Immigration Law Firms",
      description: "Professional legal representation for immigration matters",
      items: [
        "Assistance with residence permit applications",
        "Work permit and Blue Card applications",
        "Family reunification procedures",
        "Appeals against negative decisions",
        "Legal consultation on immigration matters"
      ]
    },
    {
      title: "Free Legal Aid",
      description: "Non-profit organizations providing free legal assistance",
      items: [
        "Association for Legal Intervention (SIP)",
        "Helsinki Foundation for Human Rights",
        "Legal Clinics at Polish Universities",
        "Caritas Legal Aid Centers",
        "Municipal Free Legal Aid Points"
      ]
    },
    {
      title: "Legal Documentation Services",
      description: "Help with document preparation and translation",
      items: [
        "Document translation services",
        "Notary services",
        "Document legalization",
        "Preparation of legal documents",
        "Assistance with forms and applications"
      ]
    },
    {
      title: "Specialized Legal Support",
      description: "Legal assistance for specific situations",
      items: [
        "Asylum and refugee matters",
        "Business immigration",
        "Student visas and permits",
        "Citizenship applications",
        "Emergency legal assistance"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Scale className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Legal Support Options
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Find legal assistance and immigration lawyers in Poland
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {legalServices.map((service, index) => (
            <Card key={index} className="hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                <CardDescription>{service.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-2">
                      <div className="h-6 w-6 flex items-center justify-center">
                        <div className="h-2 w-2 bg-primary rounded-full"></div>
                      </div>
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 bg-card rounded-lg p-6 border border-border">
          <h2 className="text-2xl font-bold text-foreground mb-4">Finding the Right Legal Support</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Briefcase className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Look for lawyers with specific experience in immigration law and a track record of handling cases similar to yours.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Request a consultation to discuss your case and understand the legal process, timeline, and costs involved.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Ensure your lawyer can communicate in a language you understand well and is registered with the Polish Bar Association.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/knowledge-base/support-resources')}
            className="text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Support Resources
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalSupport; 